import React from "react";
import { Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";

import DataGridActions from "./DataGridActions";
import useDataGridActions from "../../hooks/useDataGridActions";

const MightyDataGrid = ({
  entityName,
  columns = [],
  rows = [],
  onRowClick,
  showActions = false,
  actions,
  actionConfig = {},
  pagination = true,
  paginationMode = "client",
  loading = false,
  totalRows,
  pageSize = 25,
  page = 0, // Current page for server-side pagination
  onPageChange,
  onPageSizeChange,
  containerSx = {},
  useViewportHeight = true, // New prop to enable/disable viewport-based height
  viewportOffset = 420, // Default offset for typical header/button space
  ...otherProps
}) => {
  const theme = useTheme();
  const { createEditDeleteActions } = useDataGridActions();

  // Column visibility persistence
  const storageKey = `${entityName}DataGridColumns`;
  const storedModel = localStorage.getItem(storageKey);
  const [visibleModel, setVisibilityModel] = React.useState(
    storedModel ? JSON.parse(storedModel) : {},
  );

  // Internal pagination state only for client-side pagination
  const [internalPaginationModel, setInternalPaginationModel] = React.useState({
    page: 0,
    pageSize: pageSize,
  });

  // Determine the pagination model to use
  const paginationModel = React.useMemo(() => {
    if (paginationMode === "server") {
      // Server-side: always use external props - no internal state
      return {
        page: page || 0,
        pageSize: pageSize,
      };
    } else {
      // Client-side: use internal state
      return internalPaginationModel;
    }
  }, [paginationMode, page, pageSize, internalPaginationModel]);

  // Sync internal pagination model for client-side pagination
  React.useEffect(() => {
    if (paginationMode === "client") {
      setInternalPaginationModel((prev) => ({
        ...prev,
        pageSize: pageSize,
      }));
    }
  }, [pageSize, paginationMode]);

  // Handle pagination changes
  const handlePaginationModelChange = React.useCallback(
    (newModel) => {


      if (paginationMode === "server") {
        // For server-side pagination, DON'T update internal state
        // Just call parent callbacks and let external state flow back in
        if (onPageChange && newModel.page !== paginationModel.page) {

          onPageChange(newModel.page);
        }
        if (
          onPageSizeChange &&
          newModel.pageSize !== paginationModel.pageSize
        ) {
          console.log(
            "📋 MightyDataGrid: Calling onPageSizeChange",
            newModel.pageSize,
          );
          onPageSizeChange(newModel.pageSize);
        }
        // Do NOT call setInternalPaginationModel for server-side pagination
      } else {
        // For client-side pagination, update internal state

        setInternalPaginationModel(newModel);

        // Still call parent callbacks if provided (for tracking/analytics)
        if (onPageChange && newModel.page !== paginationModel.page) {
          onPageChange(newModel.page);
        }
        if (
          onPageSizeChange &&
          newModel.pageSize !== paginationModel.pageSize
        ) {
          onPageSizeChange(newModel.pageSize);
        }
      }
    },
    [onPageChange, onPageSizeChange, paginationModel, paginationMode],
  );

  // Handle column visibility changes
  const handleColumnVisibilityModelChange = (model) => {
    localStorage.setItem(storageKey, JSON.stringify(model));
    setVisibilityModel(model);
  };

  // Create the final columns array with optional actions column
  const finalColumns = React.useMemo(() => {
    const allColumns = [...columns];

    if (showActions && (actions || actionConfig)) {
      const actionsColumn = {
        headerAlign: "center",
        align: "center",
        minWidth: 60,
        field: "actions",
        headerName: "",
        sortable: false,
        hideable: false,
        filterable: false,
        renderCell: (params) => {
          let columnActions;

          if (actions) {
            // Use custom actions if provided
            columnActions =
              typeof actions === "function" ? actions(params.row) : actions;
          } else if (actionConfig) {
            // Use standard edit/delete actions with config
            columnActions = createEditDeleteActions({
              onEdit: actionConfig.onEdit,
              onDelete: actionConfig.onDelete,
              entityName: actionConfig.entityName || entityName,
              pageName: actionConfig.pageName,
              tabName: actionConfig.tabName,
            });
          }

          return <DataGridActions item={params.row} actions={columnActions} />;
        },
      };

      allColumns.push(actionsColumn);
    }

    return allColumns;
  }, [
    columns,
    showActions,
    actions,
    actionConfig,
    entityName,
    createEditDeleteActions,
  ]);

  // Default container styles
  const defaultContainerSx = {
    height: useViewportHeight
      ? `${window.innerHeight - viewportOffset}px`
      : "100%",
    width: "100%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  // Default DataGrid styles
  const defaultDataGridSx = {
    ".MuiDataGrid-sortIcon": {
      color: theme.palette.primary.main,
    },
    ".MuiDataGrid-filterIcon": {
      color: theme.palette.primary.main,
    },
    ".MuiDataGrid-menuIconButton": {
      color: theme.palette.primary.main,
    },
    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
      py: "8px",
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
      py: "15px",
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
      py: "22px",
    },
    height: "100%", // Fill the container's height
    width: "100%",
    minWidth: 0,
  };

  return (
    <Box sx={{ ...defaultContainerSx, ...containerSx }}>
      <DataGrid
        sx={defaultDataGridSx}
        columnVisibilityModel={visibleModel}
        disableSelectionOnClick
        rows={rows}
        columns={finalColumns}
        loading={loading}
        autoHeight={false}
        getRowHeight={() => "auto"}
        onRowClick={onRowClick ? (params) => onRowClick(params.row) : undefined}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        // Pagination props - only include if pagination is enabled
        {...(pagination
          ? {
              paginationMode: paginationMode,
              rowCount: totalRows,
              pageSizeOptions: [5, 10, 25, 50],
              paginationModel: paginationModel,
              onPaginationModelChange: handlePaginationModelChange,
            }
          : {})}
        // Allow any other DataGrid props to be passed through
        {...otherProps}
      />
    </Box>
  );
};

export default MightyDataGrid;
